import { updateDoc, doc, type DocumentData, Timestamp } from 'firebase/firestore';
import { UseFormWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { db } from '../../config';
import { getResult } from './read';
import { AiOcrResultFiledType, ItemFiledType } from '../../../../types/aiOcrResult';
import { PatternType, ItemListType, ItemListChildType } from '../../../../types/pattern';
import { createResultHistory } from './history';
import { ResultHistoryActionType } from '../../../../types/resultHistory';
import { detectChangedFields, detectStatusChange } from '../../../../utils/resultHistoryUtils';
import { generateSearchTextFromResultData } from '../../../../utils/generateSearchText';
import { toRowPositionByRowId } from '../../../../utils/pattern/detailRowKeys';
import { buildReviewSummary, evaluateReviewIssues } from '../../../../utils/pattern/reviewIssues';
import { matchReason } from '../../../../constants/enum';
import { MatchFilterValue } from '../../../../types/masterReconciliation';
import { toFormFieldName } from '../../../../utils/pattern/formFieldName';
import {
  arrayCalculationCellKey,
  type CalculationCellStates,
} from '../../../../utils/pattern/resultCalculation';
import { compactResultData } from '../../../../utils/pattern/compactResultData';

/**
 * 履歴差分の比較用に、保存済みデータの result_data も compact 形にそろえる（#3311）。
 *
 * 保存側は compactResultData を通すため、既定値キーを持つ移行前のレコードと
 * そのまま比較すると「値は同じなのにキー構成だけ違う」状態になり、
 * 中身が変わっていない更新でも EDIT 履歴が作られてしまう。
 * 比較の前に両側を同じ形にそろえることで、実際の値の変化だけを差分として拾う。
 *
 * functions 側にも同じ関数がある（functions/src/firestore/results.ts）。
 * 保存経路が Functions（OCRパイプライン）とフロント（ユーザー編集）に
 * 分かれているため、どちらから保存しても同じ比較になるよう両方に置いている。
 */
const normalizeForHistoryDiff = (data: DocumentData): DocumentData => {
  if (data?.result_data === undefined) return data;
  return { ...data, result_data: compactResultData(data.result_data) };
};

/** 日付値を outputFormat の文字列に揃える。
 *  OCRパイプラインは formatDateFieldValues で文字列保存しており、Algoliaのocr_facets/search_textも
 *  文字列前提で構築されるため、ユーザー編集時も同じ文字列形式に揃えて検索整合性を保つ。 */
const DEFAULT_DATE_FORMAT = 'YYYY/MM/DD';
const formatDateValueForStorage = (value: unknown, outputFormat?: string): string | null => {
  const fmt = outputFormat || DEFAULT_DATE_FORMAT;
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d.format(fmt) : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = dayjs(trimmed);
    return d.isValid() ? d.format(fmt) : trimmed;
  }
  if (typeof value === 'object' && value && 'seconds' in (value as Record<string, unknown>)) {
    const seconds = (value as { seconds: number }).seconds;
    const d = dayjs(seconds * 1000);
    return d.isValid() ? d.format(fmt) : null;
  }
  return null;
};

// 動的フォームフィールドの型定義
export interface DynamicFieldValue {
  [key: string]: string | number | Date | null;
}

export interface DynamicFormData {
  [key: string]: DynamicFieldValue[];
}

export interface EditResultRequest {
  resultId: string;
  data: Partial<DocumentData>;
  userId?: string;
}

/**
 * resultレコードを更新
 */
export const editResult = async ({ resultId, data, userId }: EditResultRequest): Promise<void> => {
  try {
    // 既存データを取得
    const existingResult = await getResult(resultId);
    if (!existingResult) {
      throw new Error('更新対象のデータが見つかりません');
    }

    const updateData: DocumentData = {
      ...data,
      updated_at: Timestamp.now(),
    };

    if (userId) {
      updateData.updated_by = userId;
    }

    const resultRef = doc(db, 'results', resultId);
    await updateDoc(resultRef, updateData);

    // 履歴記録: ステータス変更または編集（失敗してもメイン処理は継続、ユーザーには通知しない）
    try {
      const { isStatusChanged, changedFields, previousValues } = detectStatusChange(
        normalizeForHistoryDiff(existingResult),
        updateData,
      );

      if (isStatusChanged) {
        // ステータス変更
        await createResultHistory({
          result_id: resultId,
          action_type: ResultHistoryActionType.STATUS_CHANGE,
          changed_fields: changedFields,
          previous_values: previousValues,
          user_id: userId || existingResult.user_id || '',
          user_name: existingResult.user_name || 'Unknown',
        });
      } else {
        // 通常の編集
        const { changedFields: allChangedFields, previousValues: allPreviousValues } =
          detectChangedFields(normalizeForHistoryDiff(existingResult), updateData);

        if (Object.keys(allChangedFields).length > 0) {
          await createResultHistory({
            result_id: resultId,
            action_type: ResultHistoryActionType.EDIT,
            changed_fields: allChangedFields,
            previous_values: allPreviousValues,
            user_id: userId || existingResult.user_id || '',
            user_name: existingResult.user_name || 'Unknown',
          });
        }
      }
    } catch (historyError) {
      // 履歴記録の失敗はメイン処理を止めない
    }
  } catch (error) {
    throw new Error('resultレコードの更新に失敗しました');
  }
};

/**
 * resultドキュメントの更新処理
 */
export const handleResultsProcess = async (
  resultId: string,
  watch: UseFormWatch<AiOcrResultFiledType>,
  user?: { uid?: string } | null,
  status?: number | null,
  resultStatus?: number | null,
): Promise<void> => {
  const existingData = await getResult(resultId);
  if (!existingData) throw new Error('更新できませんでした。');

  const updateData: {
    processing_status?: number | null;
    status?: number | null;
    updated_at: Timestamp;
    updated_by: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result_data: any;
  } = {
    processing_status: status,
    status: resultStatus,
    updated_at: Timestamp.now(),
    updated_by: user?.uid || '',
    result_data: { ...existingData.result_data },
  };

  const updateIfChanged = (
    field: keyof AiOcrResultFiledType,
    path: string,
    isNumber = false,
  ): void => {
    const value = watch(field);
    if (value !== undefined && value !== null) {
      updateData.result_data[path] = {
        ...updateData.result_data[path],
        value: isNumber ? Number(value) : value,
      };
    }
  };

  updateIfChanged('clientId', 'client_id');
  updateIfChanged('clientName', 'client_name');
  updateIfChanged('postCode', 'post_code');
  updateIfChanged('address', 'address');
  updateIfChanged('invoiceNumber', 'invoice_number');
  updateIfChanged('totalAmount', 'total_amount', true);
  updateIfChanged('clientUnmatchFlg', 'client_unmatch_flg');

  // list の処理
  const listItems = watch('list');
  if (listItems && Array.isArray(listItems)) {
    updateData.result_data.list = listItems.map((item: ItemFiledType | null, index: number) => {
      // 既存のitemIdを保持（存在する場合）
      const existingItem = existingData.result_data?.list?.[index];
      return {
        product_name: {
          value: item?.productName || '',
          itemId: existingItem?.product_name?.itemId || null,
        },
        quantity: {
          value: Number(item?.quantity) || 0,
          itemId: existingItem?.quantity?.itemId || null,
        },
        unit_price: {
          value: Number(item?.unitPrice) || 0,
          itemId: existingItem?.unit_price?.itemId || null,
        },
        amount: {
          value: Number(item?.amount) || 0,
          itemId: existingItem?.amount?.itemId || null,
        },
        remarks: {
          value: item?.remarks || '',
          itemId: existingItem?.remarks?.itemId || null,
        },
        product_unmatch_flg: {
          value: item?.productUnmatchFlg || false,
          itemId: existingItem?.product_unmatch_flg?.itemId || null,
        },
      };
    });
  }

  // editResult関数を使用してドキュメントを更新
  await editResult({
    resultId,
    data: updateData,
    userId: user?.uid,
  });
};

/**
 * マスタ再照合で手動選択した照合結果（保存時に result_data へ反映する）（#3078）。
 * 選択時点では Firestore を更新せず、保存契機でこの情報を渡してラベル・スコア・値を確定する。
 */
export interface SelectedMasterEntry {
  /**
   * 照合キー項目の item_id。ラベル/スコアの書き込み先として使う。
   * 値そのものは appliedValues 側に含まれる（#3274）。
   * appliedValues に無い場合はラベル/スコアのみを書き込む。
   */
  searchItemId?: string;
  /** 反映対象の行インデックス（配列項目の場合。単一値は 0）。 */
  rowIndex: number;
  /** 照合理由ラベル（例："再照合（照合結果：〇〇）"）。 */
  matchReasonLabel: string;
  /** スコアラベル（例："85%"）。無ければ設定しない。 */
  score?: string;
  /**
   * 照合キー項目の上書き前の OCR 読取値（#3274）。
   * 照合キーはフォーム値ごとマスタ値へ書き換わっており、保存時点の result_data から
   * 元の読取値を復元できないため、モーダルを開いた時点の値をここで受け取る。
   */
  searchItemOcrValue?: string;
  /** 選択で埋める値（column_mappings 由来の item_id → value）。 */
  appliedValues: { itemId: string; value: unknown }[];
  /** 再照合で使った絞り込みの値（#3517）。照合キー項目に match_filter_values として書き込む。 */
  matchFilterValues?: MatchFilterValue[];
}

/** 保存時の要注意の再判定に使う照合設定（#3309） */
export interface ReviewMatchSettings {
  /** 照合キーの項目ID。照合由来の要注意をこの項目だけに絞る */
  searchItemIds: Set<string>;
  /** 項目IDごとの要注意にする下限スコア。未設定の項目は既定値を使う */
  reviewThresholds: Map<string, number>;
  /** 辞書変換の照合キーの項目ID。要注意の文言をマスタ／辞書で分けるのに使う（#3340） */
  dictionarySearchItemIds: Set<string>;
}

/**
 * 読取パターン用のresultレコードを更新
 */
export const updatePatternResult = async (
  resultId: string,
  patternData: PatternType,
  watch: UseFormWatch<Record<string, unknown>>,
  dynamicFormData: DynamicFormData,
  nextStatus: number,
  userId?: string,
  // 紐付け解除（D-2）でラベル/スコアを消す対象。
  // 単一値: `${itemId}`、配列子項目: `${childId}.${行ID}`（#3283）。
  clearedReasonItemKeys?: Set<string>,
  // マスタ再照合で手動選択した照合結果（保存時に result_data へ反映する）（#3078）。
  selectedMasterEntries?: SelectedMasterEntry[],
  // 計算セルの表示状態（#3172）。画面での再計算結果（計算値採用/フォールバック・エラー）を
  // is_calculated / calculation_error として保存し、再表示時の読み取り専用判定と一致させる。
  calculationCellStates?: CalculationCellStates,
  // 保存時の要注意の再判定に使う照合設定（#3309）。
  // 未指定なら照合由来は作り直さず、保存済みの判定を引き継ぐ。
  reviewMatchSettings?: ReviewMatchSettings,
): Promise<void> => {
  // 現在のresult_dataを取得
  const existingResult = await getResult(resultId);
  if (!existingResult?.result_data) throw new Error('データの取得に失敗しました。');

  const updatedResultData = { ...existingResult.result_data };

  // 単一値フィールドの更新
  if (patternData.item_list) {
    patternData.item_list.forEach((itemGroup: ItemListType) => {
      if (itemGroup.type !== 'array' && itemGroup.children.length === 0) {
        const fieldName = toFormFieldName(itemGroup.parentValue);
        const formValue = watch(fieldName);

        if (formValue !== undefined) {
          // 既存のデータ構造を保持しつつ、valueのみ更新（IDをキーとして使用）
          const existingFieldData = updatedResultData[itemGroup.id] || {};
          let convertedValue: unknown = formValue;

          // 数値型の場合は適切な変換を行う（カンマ除去してから数値変換）
          if (itemGroup.type === 'number') {
            // nullまたは空文字の場合はnullを保持
            if (formValue === null || formValue === undefined || formValue === '') {
              convertedValue = null;
            } else {
              const strValue = String(formValue).replace(/,/g, '');
              const numValue = parseFloat(strValue);
              convertedValue = isNaN(numValue) ? null : numValue;
            }
          } else if (itemGroup.type === 'date') {
            // 日付型は outputFormat に揃えた文字列として保存する
            // (OCRパイプラインも文字列保存。Algoliaのocr_facets/search_textも文字列前提)
            convertedValue = formatDateValueForStorage(formValue, itemGroup.outputFormat);
          }

          const cleared = clearedReasonItemKeys?.has(itemGroup.id);
          const calcState =
            itemGroup.is_calculated && itemGroup.formula
              ? calculationCellStates?.[itemGroup.id]
              : undefined;
          updatedResultData[itemGroup.id] = {
            ...existingFieldData,
            value:
              itemGroup.type === 'date' || itemGroup.type === 'number'
                ? convertedValue
                : convertedValue || '',
            // 紐付け解除済みなら照合理由ラベル/スコアを消す。
            // コード（#3309）も一緒に消さないと、判定がコードを最優先するため
            // 解除したのに OCR 時の要注意が残り続ける（#3328）。
            ...(cleared
              ? { match_reason_label: '', match_reason: '', score: '', match_filter_values: [] }
              : {}),
            // 計算セルの状態（#3172）。計算値採用/フォールバックの別とエラー理由を保存する。
            // calculation_error には画面の案内文言ではなくエラー理由を入れ、サーバ側適用（#3171）と
            // 同じ意味に揃える（Webhook / 外部APIのレスポンスにもそのまま出るため）。
            ...(calcState
              ? {
                  is_calculated: calcState.isCalculated,
                  calculation_error: calcState.errorReason ?? '',
                }
              : {}),
          };
        }
      }
    });
  }

  // 配列フィールド（テーブルデータ）の更新
  if (patternData.item_list) {
    patternData.item_list.forEach((itemGroup: ItemListType) => {
      if (itemGroup.type === 'array' || itemGroup.children.length > 0) {
        const dynamicData = dynamicFormData[itemGroup.parentValue];
        if (dynamicData && Array.isArray(dynamicData)) {
          updatedResultData[itemGroup.id] = dynamicData.map((item, displayIndex: number) => {
            const updatedItem: Record<string, unknown> = {};

            itemGroup.children.forEach((childField: ItemListChildType) => {
              const fieldKey = childField.childValue;
              const newValue = item[fieldKey];

              // item.indexを使用して既存データを特定（行挿入に対応）
              const originalIndex = typeof item.index === 'number' ? item.index : -1;
              const existingItemData =
                originalIndex >= 0 &&
                existingResult.result_data[itemGroup.id]?.[originalIndex]?.[childField.id]
                  ? existingResult.result_data[itemGroup.id][originalIndex][childField.id]
                  : {};

              let convertedValue: unknown = newValue;
              if (childField.type === 'number') {
                // nullの場合はnullを保持、それ以外はカンマ除去して数値に変換
                if (newValue === null || newValue === undefined) {
                  convertedValue = null;
                } else {
                  const strValue = String(newValue).replace(/,/g, '');
                  const numValue = parseFloat(strValue);
                  convertedValue = isNaN(numValue) ? null : numValue;
                }
              } else if (childField.type === 'date') {
                // 日付型は outputFormat に揃えた文字列として保存する
                convertedValue = formatDateValueForStorage(newValue, childField.outputFormat);
              } else {
                convertedValue = newValue || '';
              }

              const childCleared =
                originalIndex >= 0 &&
                clearedReasonItemKeys?.has(`${childField.id}.${originalIndex}`);
              // 計算セルの状態キーは行ID ベース（#3283）。画面側と揃える
              const childCalcState =
                childField.is_calculated && childField.formula
                  ? calculationCellStates?.[
                      arrayCalculationCellKey(
                        itemGroup.id,
                        originalIndex >= 0 ? originalIndex : displayIndex,
                        childField.id,
                      )
                    ]
                  : undefined;
              updatedItem[childField.id] = {
                ...existingItemData,
                value:
                  childField.type === 'date' || childField.type === 'number'
                    ? convertedValue
                    : convertedValue || '',
                // 紐付け解除済みなら照合理由ラベル/スコアを消す（コードも同様。#3328）。
                ...(childCleared
                  ? { match_reason_label: '', match_reason: '', score: '', match_filter_values: [] }
                  : {}),
                // 計算セルの状態（#3172）。計算値採用/フォールバックの別とエラー理由を保存する。
                // calculation_error は画面の案内文言ではなくエラー理由（サーバ側適用と同じ意味）。
                ...(childCalcState
                  ? {
                      is_calculated: childCalcState.isCalculated,
                      calculation_error: childCalcState.errorReason ?? '',
                    }
                  : {}),
              };
            });

            return updatedItem;
          });
        }
      }
    });
  }

  // マスタ再照合で手動選択した照合結果を result_data へ反映する（#3078）。
  // 選択時点では Firestore を更新せず、この保存契機でラベル・スコア・値を確定する。
  // 上の value 反映（フォーム値）より後に適用し、照合で埋めた値を優先させる。
  if (selectedMasterEntries && selectedMasterEntries.length > 0) {
    // 上書き前の value を ocr_value に退避する（未保存時のみ）。「OCR結果：」表示に使う。
    const pickOcrValue = (existing: unknown): string | undefined => {
      if (!existing || typeof existing !== 'object') return undefined;
      const e = existing as { ocr_value?: unknown; value?: unknown };
      if (typeof e.ocr_value === 'string' && e.ocr_value !== '') return undefined; // 既に保存済み
      if (typeof e.value === 'string' && e.value !== '') return e.value;
      if (typeof e.value === 'number') return String(e.value);
      return undefined;
    };

    // item_id 単位で「値を差し替えるか（undefined ならラベル/スコアのみ）」をまとめる。
    // 照合キー（searchItemId）はラベル/スコアの書き込み先として先に undefined で登録し、
    // appliedValues に同じ item_id の値があれば上書きされて値も反映される（#3274）。
    for (const entry of selectedMasterEntries) {
      // entry.rowIndex には行ID が入る（#3283）。解除キーの照合は行ID のまま、
      // result_data の配列書き込みは現在の配列位置に変換して行う。
      const entryRowId = entry.rowIndex;
      const metaUpdates = new Map<string, unknown>();
      if (entry.searchItemId) metaUpdates.set(entry.searchItemId, undefined);
      entry.appliedValues.forEach(({ itemId, value }) => metaUpdates.set(itemId, value));

      // 照合キーの ocr_value は result_data からは復元できない（#3274）。
      // updatedResultData は既にフォーム値（＝マスタ値で上書き済み）で更新されているため、
      // 選択時に退避しておいた読取値を使う。マッピング項目は従来どおり既存値から退避する。
      const pickOcrValueFor = (itemId: string, existing: unknown): string | undefined => {
        if (itemId === entry.searchItemId) {
          const saved = entry.searchItemOcrValue;
          if (saved === undefined || saved === '') return undefined;
          // 既に ocr_value が保存済みなら最初の読取値を優先して維持する。
          const e = (existing ?? {}) as { ocr_value?: unknown };
          if (typeof e.ocr_value === 'string' && e.ocr_value !== '') return undefined;
          return saved;
        }
        return pickOcrValue(existing);
      };

      metaUpdates.forEach((value, itemId) => {
        const writesValue = value !== undefined;
        for (const itemGroup of patternData.item_list ?? []) {
          // 単一値項目
          if (
            itemGroup.id === itemId &&
            itemGroup.type !== 'array' &&
            itemGroup.children.length === 0
          ) {
            // 紐付け解除対象ならラベルは書かない（解除を優先）。
            if (clearedReasonItemKeys?.has(itemId)) return;
            const existing = (updatedResultData[itemId] ?? {}) as Record<string, unknown>;
            const nextField: Record<string, unknown> = { ...existing };
            if (writesValue) {
              const ocrValueToSave = pickOcrValueFor(itemId, existing);
              if (ocrValueToSave !== undefined) nextField.ocr_value = ocrValueToSave;
              nextField.value = value;
            }
            nextField.match_reason_label = entry.matchReasonLabel;
            // 照合理由コードも一緒に更新する（#3328）。
            // 判定は match_reason コードを最優先する（#3309）ため、ラベルだけ
            // 書き換えると OCR 時の古いコードが残り、再照合で選び直しても
            // 「マスタに該当なし」などの要注意が消えない。
            // 再照合は人が候補を見て確定させた選択なので確認済みとして扱う。
            nextField.match_reason = matchReason.confirmed;
            if (entry.score !== undefined) nextField.score = entry.score;
            // 照合時の絞り込みの値（#3517）。前回の照合の値が残らないよう、未設定なら空にする
            nextField.match_filter_values = entry.matchFilterValues ?? [];
            updatedResultData[itemId] = nextField;
            return;
          }
          // 配列子項目
          const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
          if (childField) {
            if (clearedReasonItemKeys?.has(`${itemId}.${entryRowId}`)) return;
            const parentKey = itemGroup.id;
            const existingArr = Array.isArray(updatedResultData[parentKey])
              ? [...(updatedResultData[parentKey] as Record<string, unknown>[])]
              : [];
            // updatedResultData は dynamicFormData の現在の並びで作り直した配列なので、
            // 行ID を現在の配列位置へ変換して書き込む（#3283）
            const targetRow =
              toRowPositionByRowId(dynamicFormData[itemGroup.parentValue]).get(entryRowId) ??
              entryRowId;
            while (existingArr.length <= targetRow) existingArr.push({});
            const row = { ...(existingArr[targetRow] ?? {}) } as Record<string, unknown>;
            const cell = { ...((row[itemId] ?? {}) as Record<string, unknown>) };
            if (writesValue) {
              const ocrValueToSave = pickOcrValueFor(itemId, cell);
              if (ocrValueToSave !== undefined) cell.ocr_value = ocrValueToSave;
              cell.value = value;
            }
            cell.match_reason_label = entry.matchReasonLabel;
            // 単一項目と同じ理由で照合理由コードも更新する（#3328）
            cell.match_reason = matchReason.confirmed;
            if (entry.score !== undefined) cell.score = entry.score;
            // 照合時の絞り込みの値（#3517）。単一項目と同じ扱い
            cell.match_filter_values = entry.matchFilterValues ?? [];
            row[itemId] = cell;
            existingArr[targetRow] = row;
            updatedResultData[parentKey] = existingArr;
            return;
          }
        }
      });
    }
  }

  // search_textを再生成（Functions側と同じ: `${fileName} ${structuredText}`）
  const structuredText = generateSearchTextFromResultData(updatedResultData);
  const fileName = existingResult.files?.[0]?.file_name || '';
  const searchText = fileName ? `${fileName} ${structuredText}` : structuredText;

  // 要注意は保存後の値で判定し直す（#3309 / #3328）。
  //
  // 明細は保存せず詳細画面で都度判定するため（設計書 §4.8）、ここで作るのは
  // 一覧が使う needs_review と件数だけ。保存済みの明細を読み戻して引き継ぐ、
  // 行の並び替えに追従させる（#3283）、系統ごとに作り直す範囲を絞る（#3309）
  // といった処理は不要になった。
  //
  // 照合は照合キーの項目IDが取れたときだけ絞り込む。渡さずに判定すると
  // column_mappings の反映先すべてにスコア判定が走り、同じ内容の要注意が
  // 反映先の列数だけ並んでしまう（#3309）。
  const reviewSummary = buildReviewSummary(
    patternData.item_list
      ? evaluateReviewIssues(
          updatedResultData,
          patternData.item_list,
          reviewMatchSettings && reviewMatchSettings.searchItemIds.size > 0
            ? reviewMatchSettings.searchItemIds
            : undefined,
          undefined,
          reviewMatchSettings?.reviewThresholds,
          // 照合設定が取れていないときはラベルの文字列判定に戻す（#3340）。
          // 空の Set を渡すと「辞書の項目は1つも無い」と誤って断定してしまう
          reviewMatchSettings && reviewMatchSettings.searchItemIds.size > 0
            ? reviewMatchSettings.dictionarySearchItemIds
            : undefined,
        )
      : [],
  );

  // データベース更新
  // result_data は既定値キーを省いて保存する（#3311）。
  // 明細が数百行ある帳票では Firestore の1ドキュメント1MB上限を超えて保存自体が失敗するため、
  // 値が無いセルの bounding_boxes:[] / confidence:1 / page:null / is_uncertain:false を保存しない。
  // セル自体は残すので列は消えない。読み出し側はキーの有無に耐える形で読んでいる。
  const updateData = {
    processing_status: nextStatus,
    result_data: compactResultData(updatedResultData),
    search_text: searchText,
    updated_at: Timestamp.now(),
    updated_by: userId || '',
    needs_review: reviewSummary.needs_review,
    // 件数は今回 UI に出さないが、将来の件数表示に備えて保存する（設計書 §4.8）
    review_item_count: reviewSummary.review_item_count,
    review_issue_count: reviewSummary.review_issue_count,
  };

  // Firebase更新処理
  const resultRef = doc(db, 'results', resultId);
  await updateDoc(resultRef, updateData);

  // 履歴記録: 編集（失敗してもメイン処理は継続、ユーザーには通知しない）
  try {
    const { changedFields, previousValues } = detectChangedFields(
      normalizeForHistoryDiff(existingResult),
      updateData,
    );

    if (Object.keys(changedFields).length > 0) {
      await createResultHistory({
        result_id: resultId,
        action_type: ResultHistoryActionType.EDIT,
        changed_fields: changedFields,
        previous_values: previousValues,
        user_id: userId || existingResult.user_id || '',
        user_name: existingResult.user_name || 'Unknown',
      });
    }
  } catch (historyError) {
    // 履歴記録の失敗はメイン処理を止めない
  }
};
